/**
 * The two things about a tool call that are NOT words.
 *
 * What each tool is CALLED moved to `i18n/agents.ts` (`tool.*`) and is read
 * through `useAgentsI18n().toolLabel` — the chat showed `write_structure`, and an
 * owner watching Ben work cannot audit a turn written in tool names. It is a view
 * over names the platform already publishes at `/v3/agents/tools`, so it adds no
 * capability and no coupling; a tool the dictionary has never heard of falls back
 * to its own name with the underscores removed.
 *
 * What stays here is the summary of the ARGUMENTS — tenant data, never translated
 * — and the one question about a step that has no words in it at all.
 */

/**
 * The caller's own `t`, handed in.
 *
 * The same arrangement `features/auth/api/errors.ts` uses: this is a plain
 * function called from a stream handler, not a composable, so it cannot open a
 * scope of its own — and a module-level `t` would resolve against the ROOT
 * dictionary, which knows nothing about tools. The caller already has the right
 * scope; it lends it.
 */
export type Say = (key: string, named?: Record<string, unknown>) => string;

/**
 * A tool's arguments, in one line.
 *
 * The whole object goes to the expandable log after the turn; live, what helps is
 * knowing WHAT it is working on — `write_structure` says nothing,
 * `write_structure entity: orcamento` says everything.
 *
 * The values are the TENANT's: an entity code, a record id, a phone number. Only
 * the count of a nested object is ours, and it is a number and a word the reader
 * never sees on its own.
 */
export function summariseToolInput(input: unknown, say: Say): string {
	if (input === null || typeof input !== "object") return "";
	const parts: string[] = [];
	for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
		if (value === null || value === undefined || value === "") continue;
		const text =
			typeof value === "object"
				? say("steps.fields", { n: Object.keys(value as object).length })
				: String(value);
		parts.push(`${key}: ${text.length > 40 ? `${text.slice(0, 39)}…` : text}`);
		if (parts.length === 3) break;
	}
	return parts.join(" · ");
}

/**
 * Whether a step is still running.
 *
 * A call with no result yet is IN FLIGHT, and saying so is the difference between "it
 * is working" and "it stopped". Without this the list showed a call and a result
 * identically, so a slow `http_request` looked like a finished one.
 */
export function isPending(step: { output?: string | null }): boolean {
	return step.output === undefined || step.output === null;
}
